import { ReactElement, useCallback, useState } from 'react'
import { ModelMeshClickListener, useModelClickListenerV2 } from '@scene'
import { isEqual } from 'lodash-es'
import { AutoFixHigh } from '@mui/icons-material'
import { Alert, Box, Button, Stack } from '@mui/material'
import GuidedStartDialog from '@editor/pages/IFCImport/components/GuidedStartDialog'
import useIFCImportGuided from '@editor/pages/IFCImport/hooks/useIFCImportGuided'
import { coordinateEventName } from '@ui/events'
import { useControlStore } from '@editorStores'
import {
  useMarqueeSelectionListener,
  useIfcImportSelectionActive,
  useIfcSelection,
  MarqueeSelectionListener,
} from '@editorHooks'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import IFCElementForm from '../IFCElementForm'

/**
 * Editing ("Bearbeitung") tools for the active IFC model: select IFC elements in the scene to edit
 * them (IFCElementForm), or start the guided import mode.
 */
const IFCEditingSection = (): ReactElement => {
  const ifcDrawer = useIfcImportSelectionActive()
  const { startGuided } = useIFCImportGuided()
  const { selectedIfcElementGuids, setSelectedIfcElementGuids } = useIfcSelection()
  const actionMode = useControlStore(state => state.actionMode)
  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)

  // Same behavior as the toolbar play button: with a file already selected, ask whether to use it
  // or upload a new one; without a file, start the guided workflow right away.
  const [startDialogOpen, setStartDialogOpen] = useState(false)

  const isCoordinateInputMode = selectionModeKey?.startsWith(coordinateEventName)

  const handleIfcGlbModelClick = useCallback<ModelMeshClickListener>(
    (guid, event) => {
      const multiSelect = eventMatchesPointerModifier(event?.nativeEvent, 'multiSelect')
      if (multiSelect) {
        setSelectedIfcElementGuids(
          selectedIfcElementGuids.includes(guid)
            ? selectedIfcElementGuids.filter(g => g !== guid)
            : [...selectedIfcElementGuids, guid],
        )
      } else {
        setSelectedIfcElementGuids(
          selectedIfcElementGuids.length === 1 && selectedIfcElementGuids[0] === guid ? [] : [guid],
        )
      }
    },
    [selectedIfcElementGuids, setSelectedIfcElementGuids],
  )

  useModelClickListenerV2('ifc_glb_model', handleIfcGlbModelClick, {
    enabled: actionMode !== 'hide' && !isCoordinateInputMode,
    guids: null,
    enableOnIfcMode: true,
    enableOnMultiSelect: true,
  })

  const handleMarqueeSelect = useCallback<MarqueeSelectionListener>(
    (ids, objects) => {
      // Only marquee-select the IFC GLB model while "Import auswählen" mode is active (ifcDrawer).
      if (!ifcDrawer) return
      const filteredIds = ids.filter(
        (_id, index) => objects[index]?.userData?.type === 'gltf-model',
      )
      if (isEqual(selectedIfcElementGuids, filteredIds)) return
      setSelectedIfcElementGuids(filteredIds)
    },
    [ifcDrawer, selectedIfcElementGuids, setSelectedIfcElementGuids],
  )

  useMarqueeSelectionListener(handleMarqueeSelect)

  return (
    <Stack direction="column" sx={{ height: '100%', minHeight: 0 }}>
      {selectedIfcElementGuids.length > 0 ? (
        <Box
          sx={({ palette }) => ({
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',

            backgroundColor: palette.grey[100],
          })}
        >
          <IFCElementForm
            guids={selectedIfcElementGuids}
            onClose={() => setSelectedIfcElementGuids([])}
            onSelectGuids={setSelectedIfcElementGuids}
          />
        </Box>
      ) : (
        <>
          <Box sx={({ palette }) => ({ p: 2, borderBottom: `1px solid ${palette.divider}` })}>
            <Alert severity="info">
              IFC-Elemente in der Szene auswählen, um sie zu bearbeiten, oder den geführten Modus
              starten
            </Alert>
          </Box>
          <Box
            sx={({ palette }) => ({ flex: 1, minHeight: 0, backgroundColor: palette.grey[100] })}
          />
          <Box sx={({ palette }) => ({ p: 2, borderTop: `1px solid ${palette.divider}` })}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoFixHigh />}
              onClick={() => (activeIfcFileGuid ? setStartDialogOpen(true) : startGuided(false))}
              data-cy="ifc-editing-start-guided"
              fullWidth
            >
              Geführten Modus starten
            </Button>
          </Box>
        </>
      )}

      <GuidedStartDialog
        open={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        onUseCurrent={() => {
          setStartDialogOpen(false)
          startGuided(true)
        }}
        onUploadNew={() => {
          setStartDialogOpen(false)
          startGuided(false)
        }}
      />
    </Stack>
  )
}

export default IFCEditingSection
